import { debug } from '@pyranger/utils';
import type { EditorContext } from '@pyranger/types';

import { SerialTransaction } from './serialTransaction';

/**
 * Encapsulates the MicroPython/CircuitPython raw REPL protocol.
 *
 * This class manages the lifecycle of a raw REPL session:
 * 1. Acquires the device mutex (exclusive access)
 * 2. Sets up a SerialTransaction (redirects incoming data to a scoped buffer)
 * 3. Enters the raw REPL mode on the device
 * 4. Provides `exec()` to run Python commands and get structured stdout/stderr
 * 5. Exits raw REPL and releases mutex on `end()`
 *
 * Protocol reference:
 * https://docs.micropython.org/en/latest/reference/repl.html#raw-mode-and-raw-paste-mode
 *
 * Usage:
 * ```ts
 * const raw = await MpRawMode.begin(context);
 * try {
 *     const result = await raw.exec("print('hello')");
 *     // result === "hello"
 * } finally {
 *     await raw.end();
 * }
 * ```
 */
export class MpRawMode {
    private transaction: SerialTransaction;
    private context: EditorContext;
    private releaseMutex: (() => void) | null = null;
    private ended = false;
    private encoder = new TextEncoder();

    private constructor(
        context: EditorContext,
        transaction: SerialTransaction,
    ) {
        this.context = context;
        this.transaction = transaction;
    }

    /**
     * Begin a raw REPL session. This:
     * 1. Acquires the device mutex
     * 2. Creates and activates a SerialTransaction
     * 3. Interrupts any running program
     * 4. Enters raw REPL mode
     *
     * @param context - The editor context (must have a connected port)
     * @param softReboot - If true, performs a soft reboot after entering raw REPL
     * @returns A new MpRawMode instance ready for `exec()` calls
     * @throws If no port is connected, if mutex cannot be acquired, or if entering raw REPL fails
     */
    static async begin(
        context: EditorContext,
        softReboot = false,
    ): Promise<MpRawMode> {
        const port = context.ref.current.connection.port;
        if (!port) {
            throw new Error('MpRawMode: no serial port connected');
        }

        const mutex = context.ref.current.device.mutex;
        const release = await mutex.acquire();

        const transaction = new SerialTransaction();
        context.ref.current.device.activeTransaction = transaction;

        const instance = new MpRawMode(context, transaction);
        instance.releaseMutex = release;

        try {
            await instance.interruptProgram();
            await instance.enterRawRepl();

            if (softReboot) {
                await instance.softRebootInRawMode();
            }
        } catch (err) {
            instance.abort();
            throw err;
        }

        return instance;
    }

    /**
     * Execute a Python command in raw REPL mode and return the stdout output.
     * If the command produces output on stderr, an error is thrown with the stderr content.
     *
     * The raw REPL protocol for command execution:
     * 1. Wait for `>` (ready prompt)
     * 2. Send code
     * 3. Send Ctrl-D (\x04) to execute
     * 4. Read "OK" (2 chars) — command accepted
     * 5. Read stdout until \x04
     * 6. Read stderr until \x04
     *
     * @param code - Python code to execute
     * @param timeout - Maximum time to wait for execution to complete (default 10000ms)
     * @returns The stdout output (with trailing \x04 stripped)
     * @throws Error containing stderr if the command produced an error
     */
    async exec(code: string, timeout = 10000): Promise<string> {
        this.ensureActive();

        // Wait for the ready prompt
        await this.transaction.readUntil('>', 2000);

        // Send the code
        await this.write(code);

        // Send Ctrl-D to execute
        await this.write('\x04');

        // Read the 2-byte status
        const status = await this.transaction.readExactly(2, timeout);
        if (status !== 'OK') {
            throw new Error(`MpRawMode: command rejected, got "${status}"`);
        }

        // Read stdout (everything until first \x04)
        const stdoutRaw = await this.transaction.readUntil('\x04', timeout);
        const stdout = stdoutRaw.slice(0, -1); // Remove trailing \x04

        // Read stderr (everything until second \x04)
        const stderrRaw = await this.transaction.readUntil('\x04', timeout);
        const stderr = stderrRaw.slice(0, -1); // Remove trailing \x04

        if (stderr.length > 0) {
            throw new MpRawModeError(stderr, stdout);
        }

        return stdout;
    }

    /**
     * Execute code and stream stdout to a callback in real-time.
     * Used for running user scripts where output should appear in the terminal as it arrives.
     *
     * After receiving `OK`, the transaction enters a streaming mode where each chunk
     * of incoming data is forwarded to the `onData` callback until the `\x04` (EOT) marker
     * is detected, signaling the end of stdout. Then stderr is read normally.
     *
     * For scripts that run indefinitely, the EOT will only arrive when the script is
     * interrupted (e.g., via Ctrl-C sent separately to the port).
     *
     * @param code - Python code to execute
     * @param onData - Callback invoked with each chunk of stdout as it arrives
     * @returns An object with `stdout` (accumulated) and `stderr`
     */
    async execStreaming(
        code: string,
        onData: (chunk: string) => void,
    ): Promise<{ stdout: string; stderr: string }> {
        this.ensureActive();

        // Wait for ready prompt
        await this.transaction.readUntil('>', 2000);

        // Send code and execute
        await this.write(code);
        await this.write('\x04');

        // Read OK status
        const status = await this.transaction.readExactly(2, 5000);
        if (status !== 'OK') {
            throw new Error(
                `MpRawMode: command rejected in streaming mode, got "${status}"`,
            );
        }

        // Stream stdout to callback until \x04. Each chunk is forwarded as it arrives.
        // The buffer only holds enough to detect the \x04 across chunk boundaries.
        await this.transaction.readUntilStreaming('\x04', onData);

        // Read stderr until second \x04 (this should be quick)
        const stderrRaw = await this.transaction.readUntil('\x04', 5000);
        const stderr = stderrRaw.slice(0, -1);

        return { stdout: '', stderr };
    }

    /**
     * Exit raw REPL mode and release the device.
     * Sends Ctrl-B to return to normal REPL, waits for the `>>> ` prompt,
     * then releases the transaction and mutex.
     *
     * @returns The data received between Ctrl-B and the `>>> ` prompt (boot banner, etc.)
     *          including the prompt itself. Returns empty string on error.
     */
    async end(): Promise<string> {
        if (this.ended) return '';
        this.ended = true;

        let captured = '';

        try {
            // Ctrl-B: exit raw REPL
            await this.write('\x02');

            // Wait for the normal prompt to appear
            // The device may print a boot banner before the prompt
            captured = await this.transaction.readUntil('>>> ', 3000);
        } catch (err) {
            debug.editor(`MpRawMode.end() error: ${err}`);
        } finally {
            this.releaseResources();
        }

        return captured;
    }

    /**
     * Emergency cleanup — releases resources without trying to communicate with the device.
     * Use when the device is unresponsive or disconnected.
     */
    abort(): void {
        if (this.ended) return;
        this.ended = true;

        this.transaction.cancel();
        this.releaseResources();
    }

    // --- Private Methods ---

    /**
     * Interrupt any running program on the device by sending Ctrl-C twice,
     * then wait for the device to show a prompt.
     */
    private async interruptProgram(timeout = 2000): Promise<void> {
        // Send Ctrl-C twice to interrupt any running program
        await this.write('\x03\x03');

        // Wait for a prompt — the device might be in normal REPL or raw REPL
        try {
            await this.transaction.readUntil('>>> ', timeout);
        } catch {
            // If we time out, it might be in raw mode or unresponsive.
            // We'll try entering raw REPL anyway — the Ctrl-A will work from
            // either normal or a stuck state.
            debug.editor(
                'MpRawMode: no prompt after interrupt, continuing anyway',
            );
        }

        // Discard any accumulated buffer (boot messages, prompt, etc.)
        this.transaction.flush();
    }

    /**
     * Enter raw REPL mode by sending Ctrl-A and waiting for the raw REPL banner.
     */
    private async enterRawRepl(): Promise<void> {
        // Send \r to ensure we're at a clean line, then Ctrl-A to enter raw REPL
        await this.write('\r\x01');

        // Wait for raw REPL confirmation
        await this.transaction.readUntil('raw REPL; CTRL-B to exit\r\n', 2000);

        // Discard anything remaining (the `>` prompt will be consumed by exec())
        // Don't flush here — the `>` needs to stay for the first exec() call
    }

    /**
     * Perform a soft reboot while in raw mode.
     * Sends Ctrl-D (soft reboot), then Ctrl-C to interrupt auto-start,
     * then waits for raw REPL prompt again.
     */
    private async softRebootInRawMode(): Promise<void> {
        await this.write('\x04'); // Ctrl-D: soft reboot in raw mode
        await this.write('\x03'); // Ctrl-C: interrupt any auto-start script

        // Wait for raw REPL prompt after reboot
        await this.transaction.readUntil('raw REPL; CTRL-B to exit\r\n', 5000);
    }

    /**
     * Write a string to the serial port.
     */
    private async write(data: string): Promise<void> {
        const port = this.context.ref.current.connection.port;
        if (!port) {
            throw new Error('MpRawMode: port disconnected during operation');
        }

        const writer = this.getWriter(port);
        const encoded = this.encoder.encode(data);

        try {
            await writer.write(encoded);
        } catch (err) {
            throw new Error(`MpRawMode: write failed: ${err}`);
        }
    }

    /**
     * Get or create the global writer for the serial port.
     */
    private getWriter(
        port: SerialPort,
    ): WritableStreamDefaultWriter<Uint8Array> {
        const { globalWriter } = this.context.ref.current.connection;

        if (globalWriter) {
            return globalWriter;
        }

        const nextWriter = port.writable?.getWriter() ?? null;
        if (!nextWriter) {
            throw new Error('MpRawMode: port is not writable');
        }

        this.context.ref.current.connection.globalWriter = nextWriter;
        return nextWriter;
    }

    /**
     * Release the transaction and mutex.
     */
    private releaseResources(): void {
        // Deactivate the transaction so the serial router stops feeding it
        this.context.ref.current.device.activeTransaction = null;

        // Drain any remaining data — in normal end() this would be empty,
        // but after abort it might contain partial protocol data we discard
        this.transaction.drain();

        // Release the mutex so other operations can proceed
        if (this.releaseMutex) {
            this.releaseMutex();
            this.releaseMutex = null;
        }
    }

    private ensureActive(): void {
        if (this.ended) {
            throw new Error(
                'MpRawMode: session has ended, cannot execute commands',
            );
        }
    }
}

/**
 * Error thrown when a raw REPL command produces output on stderr.
 * Contains both the stderr content (as the error message) and any stdout
 * that was produced before the error.
 */
export class MpRawModeError extends Error {
    public readonly stdout: string;

    constructor(stderr: string, stdout: string) {
        super(stderr);
        this.name = 'MpRawModeError';
        this.stdout = stdout;
    }
}
